import React from 'react';
import { motion } from 'framer-motion';
import Image from 'next/image';
import Button from './ui/Button';
import { heroData } from '@/data/hero';

const Hero = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  const ambientOrbs = [
    {
      className: 'bg-soft-gold/35',
      size: 'w-48 h-48',
      style: { top: '-12rem', left: '8%' } as React.CSSProperties,
      duration: 16,
      delay: 0,
    },
    {
      className: 'bg-rose-300/25',
      size: 'w-40 h-40',
      style: { top: '15%', right: '12%' } as React.CSSProperties,
      duration: 18,
      delay: 2,
    },
    {
      className: 'bg-sky-300/20',
      size: 'w-56 h-56',
      style: { bottom: '10%', left: '18%' } as React.CSSProperties,
      duration: 20,
      delay: 1.2,
    },
  ];

  const heroHighlights = [
    {
      label: 'Placement windows',
      value: '6 families each quarter'
    },
    {
      label: 'Concierge support',
      value: 'Lifetime guidance & salon access'
    },
    {
      label: 'Wellness promise',
      value: 'Holistic nutrition + socialization rituals'
    }
  ];

  return (
    <section className="relative min-h-screen pt-28 pb-24 flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <motion.div
          className="absolute inset-0 bg-gradient-to-br from-primary-950/85 via-primary-900/65 to-primary-950/90"
          style={{ backgroundAttachment: 'fixed' }}
          animate={{ opacity: [0.75, 0.95, 0.75] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        />
        <Image
          src={heroData.image}
          alt="Beautiful Balinese Cat"
          fill
          priority
          className="object-cover object-[center_25%] sm:object-center"
          quality={100}
        />
      </div>

      {ambientOrbs.map((orb, index) => (
        <motion.div
          key={index}
          className={`pointer-events-none absolute rounded-full blur-3xl ${orb.size} ${orb.className}`}
          style={orb.style}
          animate={{
            opacity: [0.15, 0.55, 0.15],
            scale: [0.9, 1.08, 0.95],
            x: [0, 10, -10],
          }}
          transition={{ duration: orb.duration, repeat: Infinity, ease: 'easeInOut', delay: orb.delay }}
        />
      ))}

      {/* Floating Particles */}
      {Array.from({ length: 15 }).map((_, index) => (
        <motion.div
          key={index}
          className="absolute rounded-full bg-white/10 blur-2xl"
          style={{
            top: `${20 + (index * 5) % 60}%`,
            left: `${10 + (index * 7) % 80}%`,
            width: `${10 + (index * 3) % 15}px`,
            height: `${10 + (index * 3) % 15}px`,
          }}
          animate={{
            y: [0, -20, 0],
            opacity: [0.2, 0.8, 0.2],
          }}
          transition={{ duration: 6 + (index * 2) % 8, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}

      {/* Content */}
      <div className="relative z-10 w-full px-6">
        <motion.div
          className="mx-auto max-w-5xl"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div
            className="relative overflow-hidden rounded-[32px] border border-white/20 bg-white/10 p-10 backdrop-blur-xl shadow-[0_30px_80px_rgba(0,0,0,0.35)]"
            variants={itemVariants}
          >
            <motion.div
              className="pointer-events-none absolute -top-20 -left-20 h-56 w-56 rounded-full bg-white/20 blur-3xl"
              animate={{ opacity: [0.15, 0.35, 0.15], scale: [0.8, 1.05, 0.85] }}
              transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.div
              className="pointer-events-none absolute bottom-[-6rem] right-[-4rem] h-72 w-72 rounded-full bg-amber-300/20 blur-[140px]"
              animate={{ opacity: [0.2, 0.4, 0.2], scale: [0.85, 1.08, 0.92] }}
              transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut', delay: 0.8 }}
            />

            <div className="relative z-10 space-y-8 text-center md:text-left">
              <div className="inline-flex items-center gap-3 rounded-full border border-white/30 bg-white/10 px-5 py-2 text-xs uppercase tracking-[0.35em] text-white/70">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/15 text-sm">★</span>
                Maison Lindy
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold text-white leading-tight">
                {heroData.title}
              </h1>
              <p className="text-lg md:text-xl text-white/85 max-w-3xl mx-auto md:mx-0">
                {heroData.subtitle}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                <Button href={heroData.primaryButton.href} variant="primary" size="lg" className="shadow-[0_20px_45px_rgba(196,166,108,0.45)]">
                  <span className="flex items-center gap-3">
                    {heroData.primaryButton.text}
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </span>
                  </span>
                </Button>
                <Button href={heroData.secondaryButton.href} variant="secondary" size="lg" className="border-white/35">
                  <span className="flex items-center gap-3">
                    {heroData.secondaryButton.text}
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7" />
                      </svg>
                    </span>
                  </span>
                </Button>
              </div>

              <div className="grid gap-4 sm:grid-cols-3 text-left">
                {heroHighlights.map((item) => (
                  <div
                    key={item.label}
                    className="rounded-2xl border border-white/20 bg-white/5 px-5 py-4 text-white/85"
                  >
                    <p className="text-xs uppercase tracking-[0.35em] text-white/60 mb-2">{item.label}</p>
                    <p className="text-sm leading-relaxed">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div 
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 cursor-pointer"
        animate={{
          y: [0, 10, 0],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      >
        <div className="w-8 h-12 border-2 border-white/50 rounded-full flex justify-center p-1">
          <motion.div 
            className="w-1 h-2 bg-white rounded-full"
            animate={{
              y: [0, 16],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
